import React, { useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { AdditionalPerson, Employee } from "@/types";
import AdditionalPeopleEditor from "./AdditionalPeopleEditor";

interface FitpassRequestEditDialogProps {
  open: boolean;
  isUpdating: boolean;
  isLoadingEmployees: boolean;
  employees: Employee[];
  editEmployee: Employee | null;
  editAdditionalPeople: AdditionalPerson[];
  onClose: () => void;
  onSave: () => void;
  onEmployeeChange: (employee: Employee | null) => void;
  onAddPerson: () => void;
  onRemovePerson: (index: number) => void;
  onPersonChange: (
    index: number,
    field: "name" | "surname" | "email",
    value: string,
  ) => void;
}

const FitpassRequestEditDialog: React.FC<FitpassRequestEditDialogProps> = ({
  open,
  isUpdating,
  isLoadingEmployees,
  employees,
  editEmployee,
  editAdditionalPeople,
  onClose,
  onSave,
  onEmployeeChange,
  onAddPerson,
  onRemovePerson,
  onPersonChange,
}) => {
  const employeeOptions = useMemo(() => {
    if (!editEmployee) {
      return employees;
    }
    if (employees.some((employee) => employee.id === editEmployee.id)) {
      return employees;
    }
    return [editEmployee, ...employees];
  }, [employees, editEmployee]);

  return (
    <Dialog
      open={open}
      onClose={() => !isUpdating && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Fitpass Request</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Autocomplete
            options={employeeOptions}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            value={editEmployee}
            onChange={(_, newValue) => onEmployeeChange(newValue)}
            disabled={isLoadingEmployees}
            renderInput={(params) => (
              <TextField {...params} label="Employee" required />
            )}
          />

          <AdditionalPeopleEditor
            people={editAdditionalPeople}
            onAdd={onAddPerson}
            onRemove={onRemovePerson}
            onChange={onPersonChange}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isUpdating || !editEmployee}
        >
          {isUpdating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FitpassRequestEditDialog;
